/** Integrations catalog + MCP server guide strings (the /app/integrations surface). */
export const enIntegrations = {
	title: "Integrations",
	subtitle: "Connect Signalist to your CRM and outreach tools",
	categories: {
		crm: "CRM",
		outreachTools: "Outreach Tools",
		aiTools: "AI Tools",
		automation: "Automation",
		developer: "Developer",
	},
	status: {
		connected: "Connected",
		notConnected: "Not connected",
		comingSoon: "Coming soon",
		available: "Available",
	},
	beta: {
		label: "Beta",
		tooltip: "This integration is in beta — you may encounter bugs",
	},
	cta: {
		manage: "Manage",
		connect: "Connect",
		setupGuide: "Setup guide",
		suggest: "Don't see your tool? Let us know",
	},
	detail: {
		back: "Back to integrations",
	},
	providers: {
		hubspot: {
			title: "HubSpot",
			description: "Sync contacts, companies and deals with HubSpot CRM",
		},
		zoho: {
			title: "Zoho CRM",
			description: "Sync leads, contacts, accounts and deals with Zoho CRM",
		},
		pipedrive: {
			title: "Pipedrive",
			description: "Sync persons, organizations and deals with Pipedrive",
		},
		salesforce: {
			title: "Salesforce",
			description: "Sync contacts, accounts and opportunities with Salesforce",
		},
		msdynamics: {
			title: "MS Dynamics 365",
			description: "Sync your CRM data with Microsoft Dynamics 365",
		},
		lemlist: {
			title: "Lemlist",
			description: "Push prospects into Lemlist multi-channel sequences",
		},
		mcp: {
			title: "MCP Server",
			description: "Connect AI assistants to Signalist over the Model Context Protocol",
		},
		n8n: {
			title: "n8n",
			description: "Automate Signalist workflows with the n8n node",
		},
		api: {
			title: "API",
			description: "Create API keys and read the API documentation",
		},
		webhooks: {
			title: "Webhooks",
			description: "Send list events to your own endpoints in real time",
		},
	},
	mcp: {
		copy: "Copy",
		copied: "Copied",
		overview: {
			title: "What is the Signalist MCP server?",
			body: "The Model Context Protocol (MCP) is an open standard that lets AI assistants and agents call your tools directly. The Signalist MCP server exposes your account's prospecting, research and knowledge-base actions to any MCP-compatible client — such as Claude or Cursor — so an assistant can search prospects, trigger deep research and read or update your knowledge DB on your behalf, without any custom integration code.",
		},
		connection: {
			title: "Endpoint & authentication",
			endpointLabel: "Endpoint",
			authNote:
				"The server speaks Streamable HTTP over POST and is stateless. Authentication is OAuth 2.1: point your client at the endpoint and it registers itself, then opens a Signalist page where you approve the connection. There is no key to generate or paste. Every action runs as you, with your permissions and feature flags — review or disconnect your assistants any time under Account → Connected Accounts.",
		},
		clients: {
			title: "Add it to your tools",
			intro:
				"Point any MCP client at the endpoint above. Each one authenticates through the same consent screen the first time it connects, so these snippets carry no credentials.",
			claudeCode: {
				title: "Claude Code",
				description: "Register the server from the terminal with the HTTP transport.",
			},
			cursor: {
				title: "Cursor",
				description: "Add the server to your ~/.cursor/mcp.json (or a project .cursor/mcp.json).",
			},
			claudeDesktop: {
				title: "Claude Desktop",
				description:
					"Claude Desktop connects to remote servers through the mcp-remote bridge. Add this to claude_desktop_config.json.",
			},
			generic: {
				title: "Any other MCP client",
				description:
					"For any client that supports Streamable HTTP, use these connection details directly.",
			},
		},
		capabilities: {
			title: "What an assistant can do",
			items: {
				search: "Search and read prospects, campaigns and lists across your company.",
				research: "Trigger deep research on a prospect and read the generated results.",
				knowledgeDb:
					"Create and update knowledge-base entries (ICPs, products, case studies, objections).",
				jobs: "Poll long-running jobs for status and results.",
			},
		},
		security: {
			title: "Security & limits",
			body: "The MCP server never sends messages to real people and cannot activate draft campaigns — it is read and internal-action only. Requests are rate limited to {{requests}} per {{seconds}} seconds per connected application. Disconnect an application at any time under Account → Connected Accounts to cut off its access immediately.",
		},
	},
	n8n: {
		overview: {
			title: "What is the Signalist n8n node?",
			body: "n8n is an open workflow-automation tool. The Signalist n8n node lets you trigger and read Signalist actions from your own automations — enrich prospects, push list events, and connect Signalist to the hundreds of other apps n8n supports, without writing integration code.",
		},
		setup: {
			title: "How to set it up",
			steps: {
				install:
					'In n8n, open Settings → Community nodes → Install and enter the package name "n8n-nodes-signalist".',
				credentials: "Add a new Signalist credential in n8n.",
				apiKey:
					"Paste a Signalist API key into the credential — create one under the API integration.",
				build: "Add the Signalist node to a workflow and pick the action you want to automate.",
			},
			openNode: "Open the n8n node",
			apiDocs: "API documentation",
			manageKeys: "Manage API keys",
		},
	},
	api: {
		docsCta: "Go to documentation",
		createCta: "Create new API key",
	},
	webhooks: {
		createCta: "Create new webhook",
	},
};
